import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line max-lines-per-function
export function BerufeFilter({ view }: { view: View }) {
  const { t } = useTranslation();
  const { store } = useRotationsplanFeatureContext();
  const {
    data: { berufe },
    ui: {
      kalender: {
        filter: { berufe: kalenderFilter },
      },
      uebersicht: {
        filter: { berufe: uebersichtFilter },
      },
    },
  } = store;
  const defaultSelected =
    view === 'planung' ? kalenderFilter : uebersichtFilter;
  const [selectedBerufe, setSelectedBerufe] =
    useState<string[]>(defaultSelected);
  const [filter, setFilter] = useState('');

  function onChangeSelectedBeruf(berufName: string) {
    if (selectedBerufe.includes(berufName)) {
      const newBerufe = selectedBerufe.filter((beruf) => beruf !== berufName);
      setSelectedBerufe(newBerufe);
      if (view === 'planung') {
        store.setKalenderBerufeFilter(newBerufe);
      } else {
        store.setUebersichtBerufeFilter(newBerufe);
      }
    } else {
      setSelectedBerufe([...selectedBerufe, berufName]);
      if (view === 'planung') {
        store.setKalenderBerufeFilter([...selectedBerufe, berufName]);
      } else {
        store.setUebersichtBerufeFilter([...selectedBerufe, berufName]);
      }
    }
  }

  function deselectAll() {
    setSelectedBerufe([]);
    if (view === 'planung') {
      store.setKalenderBerufeFilter([]);
    } else {
      store.setUebersichtBerufeFilter([]);
    }
  }

  function onChangeFilter(input: string) {
    setFilter(input);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger className="hidden md:flex" asChild>
          <FilterTrigger
            selectedBerufe={selectedBerufe}
            deselectAll={deselectAll}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="bg-white p-2 px-0 w-full md:max-w-70">
          <Command>
            <CommandInput
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            />
            <CommandList>
              {berufe
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((beruf) => (
                  <CommandItem
                    key={beruf.id}
                    value={beruf.name}
                    onSelect={(value) => onChangeSelectedBeruf(value)}>
                    <ListItem
                      beruf={beruf}
                      selectedBerufe={selectedBerufe}
                      onChangeSelectedBeruf={onChangeSelectedBeruf}
                    />
                  </CommandItem>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer>
        <DrawerTrigger className="flex md:hidden" asChild>
          <FilterTrigger
            selectedBerufe={selectedBerufe}
            deselectAll={deselectAll}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t('domain.mitarbeiter.rotationsplan.filter.berufe')}
          </div>
          <Input
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          {berufe
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter((beruf) =>
              beruf.name
                .toLocaleLowerCase()
                .includes(filter.toLocaleLowerCase()),
            )
            .map((beruf, index) => (
              <div
                className="flex flex-col gap-4"
                key={beruf.id}
                onClick={() => onChangeSelectedBeruf(beruf.name)}>
                <ListItem
                  selectedBerufe={selectedBerufe}
                  beruf={beruf}
                  onChangeSelectedBeruf={onChangeSelectedBeruf}
                />
                {index < berufe.length - 1 && (
                  <div className="w-full bg-neutrals-200 h-px"></div>
                )}
              </div>
            ))}
        </DrawerContent>
      </Drawer>
    </>
  );
}

function FilterTrigger({
  selectedBerufe,
  deselectAll,
  ...props
}: {
  selectedBerufe: string[];
  deselectAll: () => void;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  return (
    <ButtonGroup {...props}>
      <Button className="flex items-center gap-2" variant="outline-secondary">
        {t('domain.mitarbeiter.rotationsplan.filter.berufe')}
        {selectedBerufe.length > 0 && (
          <Badge type="pill" variant="secondary">
            {selectedBerufe.length}
          </Badge>
        )}
      </Button>
      {selectedBerufe.length > 0 && (
        <Button
          variant="outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            deselectAll();
          }}>
          <FontAwesomeIcon icon={faXmarkLarge} />
        </Button>
      )}
    </ButtonGroup>
  );
}

function ListItem({
  selectedBerufe,
  beruf,
  onChangeSelectedBeruf,
}: {
  selectedBerufe: string[];
  beruf: Beruf;
  onChangeSelectedBeruf: (beruf: string) => void;
}) {
  return (
    <div className="flex justify-between items-center w-full font-normal">
      <div className="whitespace-nowrap truncate text-sm">{beruf.name}</div>
      <Checkbox
        checked={selectedBerufe.includes(beruf.name)}
        onCheckedChange={() => onChangeSelectedBeruf(beruf.name)}
        className="disabled:opacity-100 disabled:cursor-auto"
      />
    </div>
  );
}
